import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useChatContext } from 'stream-chat-react';
import Loader from './components/Loader';
import React from 'react';
import { Button } from '../../components/Button';
import { ButtonVariant } from '../../config/colors';
import { MemberListTile } from './components/MemberListTile';
import { useAuth } from '../../hooks/useAuth';
import { GroupChat } from '../../models/GroupChat';
import { groupRepository } from '../../repositories/GroupRepository';
import { useConfirm } from '../../hooks/useConfirm';
import { ButtonAlignement } from '../../components/ConfirmModal';
import { LayoutWithTopBar } from '../../components/LayoutWithTopBar';
import { CopyToClipboard } from '../../components/CopyToClipboard';
import { ChatChannelType } from '../../models/ChatChannel';
import { formatAmount } from '../../utils/Utils';
import { useRouter } from '../../hooks/useRouter';
import { canManageMembers, Pleb } from '../../models/Pleb';
import { useInView } from 'react-intersection-observer';
import { eventsRepository } from '../../repositories/EventsRepository';

interface ProfilePageGroupProps {
  channelType: string;
}

const MEMBERS_LIMIT = 50;

const ProfilePageGroup: React.FC<ProfilePageGroupProps> = ({channelType}) => {
  const navigate = useNavigate();
  const { tab, groupId } = useParams();
  const { client } = useChatContext();
  const [groupChat, setGroupChat] = useState<GroupChat | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isMembersLoading, setIsMembersLoading] = useState(false);
  const [hasMoreMembers, setHasMoreMembers] = useState(true);
  const [members, setMembers] = useState<Pleb[]>([]);
  const [error, setError] = useState<Error | null>(null);
  const {pleb} = useAuth();
  const {openConfirm} = useConfirm();
  const {navigateToEvent} = useRouter();
  const { ref, inView } = useInView();
  const offsetRef = useRef(0);

  useEffect(() => {
    setIsLoading(true);

    groupRepository.getGroupDetails(groupId ?? '', channelType, pleb?.id ?? '', true)
      .then((group) => {
        setGroupChat(group);
        setError(null);
      })
      .catch((err) => {
        console.error('Error getting channel:', err);
        setError(err as Error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [groupId]);

  useEffect(() => {
    if(inView && hasMoreMembers && !isMembersLoading) {
      setIsMembersLoading(true);
      groupRepository.getGroupMembers(groupId ?? '', channelType, pleb?.id ?? '', offsetRef.current, MEMBERS_LIMIT)
        .then(({members, hasMore}) => {
          offsetRef.current += MEMBERS_LIMIT;
          setMembers(prev => [...prev, ...members]);
          setHasMoreMembers(hasMore);
        })
        .catch((err) => {
          setHasMoreMembers(false);
        })
        .finally(() => {
          setIsMembersLoading(false);
        });
    }
  }, [inView, hasMoreMembers, isMembersLoading]);

  const showDialogDelete = () => {
    openConfirm({
      message: 'Are you sure you want to delete this chat?',
      buttons: [
        {label: 'Cancel', onClick: () => {}, variant: ButtonVariant.transparent},
        {label: 'Delete', onClick: () => {handleDeleteChat()}, variant: ButtonVariant.danger}
      ],
      buttonAlignement: ButtonAlignement.row
    });
  }

  const showDialogLeave = () => {
    openConfirm({
      message: 'Are you sure you want to leave this chat ?',
      buttons: [
        {label: 'Cancel', onClick: () => {}, variant: ButtonVariant.transparent},
        {label: 'Leave', onClick: () => {handleLeaveChat()}, variant: ButtonVariant.danger}
      ],
      buttonAlignement: ButtonAlignement.row
    });
  }

  const handleDeleteChat = async () => {
    try {
      if(groupChat?.channelId) {
        await client.deleteChannels([groupChat.channelId]);
        navigate('/home/chat');
      }
    } catch (error) {
      console.error('Error deleting chat:', error);
    }
  };

  const handleLeaveChat = async () => {
    try {
      if(groupChat?.id && client.userID) {
        const channel = client.channel('messaging', groupChat.id);
        await channel.removeMembers([client.userID]);
        navigate('/home/chat');
      }
    } catch (error) {
      console.error('Error leaving chat:', error);
    }
  };

  const getShareLink = async () => {
    if(channelType === ChatChannelType.Group) {
      return groupChat?.shortLink ?? '';
    }
    else if(channelType === ChatChannelType.Event) {
      return eventsRepository.getEventShareLink(groupChat?.id ?? '');
    }
    return '';
  }

  const renderProfile = () => {
    if (isLoading) return <Loader />;
    if (error) return <div>Error loading chat details</div>;
    if (!groupChat) return null;

    return (
      <div className="p-4 flex flex-col">
        <div className="flex flex-col items-center mb-6">
          <img 
            src={groupChat.image}
            className={`object-cover w-32 h-32 rounded-full bg-white ${channelType === ChatChannelType.Event ? 'cursor-pointer' : ''}`}
            alt={groupChat.name}
            onClick={() => {
              if(channelType === ChatChannelType.Event) {
                navigateToEvent(groupChat.id);
              }
            }}
          />

          <h1 
            className={`mt-2 text-xl font-bold text-center ${channelType === ChatChannelType.Event ? 'cursor-pointer' : ''}`}
            onClick={() => {
              if(channelType === ChatChannelType.Event) {
                navigateToEvent(groupChat.id);
              }
            }}
          >
            {groupChat.name}
          </h1>

          {
            groupChat.description && (
              <p className="text-sm mt-4 bg-brand-bg-4 p-4 rounded-lg whitespace-pre-wrap">
                {groupChat.description?.replace(/\t/g, '    ')}
              </p>
            )
          }
          
          <div className="flex flex-row justify-between items-center w-full px-6 mt-3">
            <CopyToClipboard toCopy={getShareLink} className={`grow`}>
              <div 
                className="flex flex-row items-center gap-2"
              >
                <span className="material-symbols-outlined text-brand-orange">
                  link
                </span>
                <p className="text-lg text-brand-orange">
                  {channelType === ChatChannelType.Group ? 'Invite via link' : 'Share event link'}
                </p>
              </div>
            </CopyToClipboard>

            <p className="text-lg">
              {
                groupChat.memberCount > 0 && `${formatAmount(groupChat.memberCount)} member${groupChat.memberCount > 1 ? 's' : ''}`
              }
            </p>
          </div>
        </div>

        <div className="flex flex-col">
          {
            members.map(member => (
              <MemberListTile
                key={member.id}
                member={member}
                admin={groupChat.admins.includes(member.id)}
                onTap={() => navigate(`/home/${tab}/user/${member.id}`)}
                allowPressAwaitingReply={(pleb && canManageMembers(pleb)) ? true : false}
              />
            ))
          }
          {isMembersLoading && <div className="flex justify-center items-center py-4"><Loader /></div>}
          {hasMoreMembers && <div ref={ref} className="h-5" />}
        </div>

        <div className="flex flex-col items-center mt-4">
          {
            channelType === ChatChannelType.Group && (
              <div className="flex flex-row w-auto">
                {groupChat.adminPermissionsEnabled ? (
                  <Button 
                    variant={ButtonVariant.danger}
                    className="mt-1 w-auto px-4"
                    onClick={() => showDialogDelete()}
                  >
                    <i className="material-icons mr-2">delete</i>
                    Delete Chat
                  </Button>
                ) : (
                  <Button
                    variant={ButtonVariant.danger}
                    className="mt-1 w-auto px-4"
                    onClick={() => showDialogLeave()}
                  >
                    <i className="material-icons mr-2">logout</i>
                    Leave Chat
                  </Button>
                )}
              </div>
            )
          }
        </div>
        
      </div>
    );
  };


  return (
    <LayoutWithTopBar
      onClickPrevArrow={() => navigate(-1)}
      topBarChildren={
        <div className="flex items-center">
          <h1 className="text-l font-bold text-center">{groupChat?.name}</h1>
        </div>
      }
    >
      {renderProfile()}
    </LayoutWithTopBar>
  );
}

export default ProfilePageGroup;